import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Feedback, getClient } from '@sentry/react';
import { ROUTES, USER_ROLES } from '../constants';
import ViewJobPost from '../components/ViewJobPost';
import JobApplyDocuments from '../components/applyJob/JobApplyDocuments';
import BoardTabLayout from '../components/layout/BoardTabLayout';
import { BoardDashboardPage } from '../components/dashboard/BoardDashboardPage';
import CreateJob from '../components/dashboard/createJob/CreateJob';
import JobPostingDetails from '../components/dashboard/jobPostingDetails/JobPostingDetails';
import TeamMembersPage from '../components/TeamMembersPage';
import AddTeamMemberPage from '../components/AddTeamMemberPage';
import InviteAcceptancePage from '../components/InviteAcceptancePage';
import ProtectedRoute from '../components/ProtectedRoute';
import SetupRouteGuard from '../components/SetupRouteGuard';
import SettingsPage from '../components/settings/SettingsPage';
import BillingPage from '../components/billing/BillingPage';
import AnalyticsPage from '../components/analytics/AnalyticsPage';
import SalesDashboardRoute from '../components/SalesDashboardRoute';

// Onboarding Flow Components
import WelcomeScreen from '../components/onboarding/WelcomeScreen';
import LoginFlow from '../components/onboarding/LoginFlow';
import ForgotPasswordScreen from '../components/onboarding/ForgotPasswordScreen';
import CreateNewCompanyScreen from '../components/onboarding/CreateNewCompanyScreen';
import CreateCompanyEmailCheck from '../components/onboarding/CreateCompanyEmailCheck';
import CompanyProfileScreen from '../components/onboarding/setup/CompanyProfileScreen';
import BillingDetailsScreen from '../components/onboarding/setup/BillingDetailsScreen';
import UserProfileScreen from '../components/onboarding/setup/UserProfileScreen';
// Optional invite-team onboarding step kept for future use.
// import InviteTeamScreen from '../components/onboarding/setup/InviteTeamScreen';
import SetupCompleteScreen from '../components/onboarding/SetupCompleteScreen';

// Candidate Auth Components
import CandidateSignIn from '../components/candidate/CandidateSignIn';
import CandidateSignUp from '../components/candidate/CandidateSignUp';
import CandidateForgotPassword from '../components/candidate/CandidateForgotPassword';
import OAuthRedirect from '../components/candidate/OAuthRedirect';
import CandidateRouteGuard from '../components/candidate/CandidateRouteGuard';
import PublicApplicantReport from '../components/report/PublicApplicantReport';
import InterviewPortal from '../components/interview/InterviewPortal';
import LiveInterviewPreview from '../components/interview/LiveInterviewPreview';
import SalesDashboardPage from '../components/sales/SalesDashboardPage';
import SalesJobLookupPage from '../components/sales/SalesJobLookupPage';
import SalesLoginPage from '../components/sales/SalesLoginPage';
import {
  PLG_MODE,
  SALES_DASHBOARD_PATH,
  SALES_JOB_QUERY_PATH,
} from '../constants/salesDashboard';

export default function App() {
  const location = useLocation();
  const isFeedbackActorVisibleRef = useRef(false);
  const isLiveInterviewPreview =
    import.meta.env.DEV && location.pathname.startsWith('/__interview-preview');
  const isInterviewRoute = location.pathname.startsWith('/interview');

  // Sentry's feedback actor runs with autoInject disabled, so this effect owns
  // its lifecycle: hidden on board surfaces (Anton provides the in-app "Report
  // a Bug" entry point there) and shown on every other surface. We act only on
  // the board <-> non-board transition so navigating between two non-board (or
  // two board) routes doesn't needlessly tear down and rebuild the actor.
  useEffect(() => {
    const client = getClient();
    const feedback = client?.getIntegration(Feedback);

    if (!feedback) {
      return;
    }

    const isBoardRoute = location.pathname.startsWith(ROUTES.BOARD);

    if (isLiveInterviewPreview || isInterviewRoute) {
      feedback.remove();
      isFeedbackActorVisibleRef.current = false;
      return;
    }

    if (isBoardRoute) {
      if (isFeedbackActorVisibleRef.current) {
        feedback.remove();
        isFeedbackActorVisibleRef.current = false;
      }
      return;
    }

    if (!isFeedbackActorVisibleRef.current) {
      // remove() first clears any stale, actor-less widget left behind by
      // Anton's openDialog() on a board route, so we always end up with exactly
      // one clean actor. This only runs on the transition into a non-board
      // route, not on every navigation.
      feedback.remove();
      feedback.createWidget();
      isFeedbackActorVisibleRef.current = true;
    }
  }, [isInterviewRoute, isLiveInterviewPreview, location.pathname]);

  if (isLiveInterviewPreview) {
    return <LiveInterviewPreview />;
  }

  if (PLG_MODE) {
    return (
      <CandidateRouteGuard>
        <Routes>
          <Route path={ROUTES.LOGIN} element={<SalesLoginPage />} />
          <Route
            path={ROUTES.FORGOT_PASSWORD}
            element={<ForgotPasswordScreen />}
          />
          <Route path={ROUTES.OAUTH_REDIRECT} element={<OAuthRedirect />} />
          <Route
            path={SALES_DASHBOARD_PATH}
            element={
              <SalesDashboardRoute>
                <SalesDashboardPage />
              </SalesDashboardRoute>
            }
          />
          <Route
            path={SALES_JOB_QUERY_PATH}
            element={
              <SalesDashboardRoute>
                <SalesJobLookupPage />
              </SalesDashboardRoute>
            }
          />
          <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
        </Routes>
      </CandidateRouteGuard>
    );
  }

  return (
    <CandidateRouteGuard>
      <Routes>
        {/* Onboarding Flow Routes */}
        <Route path={ROUTES.HOME} element={<WelcomeScreen />} />
        <Route path={ROUTES.LOGIN} element={<LoginFlow />} />
        <Route
          path={ROUTES.FORGOT_PASSWORD}
          element={<ForgotPasswordScreen />}
        />
        <Route path={ROUTES.INVITE} element={<InviteAcceptancePage />} />

        {/* Candidate Auth Routes */}
        <Route path={ROUTES.CANDIDATE_SIGNIN} element={<CandidateSignIn />} />
        <Route path={ROUTES.CANDIDATE_SIGNUP} element={<CandidateSignUp />} />
        <Route
          path={ROUTES.CANDIDATE_FORGOT_PASSWORD}
          element={<CandidateForgotPassword />}
        />
        <Route path={ROUTES.OAUTH_REDIRECT} element={<OAuthRedirect />} />

        {/* New Company Creation */}
        <Route
          path={ROUTES.CREATE_COMPANY_CHECK}
          element={<CreateCompanyEmailCheck />}
        />
        <Route
          path={ROUTES.CREATE_COMPANY}
          element={<CreateNewCompanyScreen />}
        />

        {/* Setup Flow Routes - Protected routes */}
        <Route
          path={ROUTES.SETUP}
          element={
            <ProtectedRoute>
              <SetupRouteGuard>
                <CompanyProfileScreen />
              </SetupRouteGuard>
            </ProtectedRoute>
          }
        >
          <Route
            path="billing"
            element={
              <ProtectedRoute>
                <SetupRouteGuard>
                  <BillingDetailsScreen />
                </SetupRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="profile"
            element={
              <ProtectedRoute>
                <SetupRouteGuard>
                  <UserProfileScreen />
                </SetupRouteGuard>
              </ProtectedRoute>
            }
          />
          {/* Optional invite-team onboarding step kept for future use.
          <Route
            path="team"
            element={
              <ProtectedRoute>
                <SetupRouteGuard>
                  <InviteTeamScreen />
                </SetupRouteGuard>
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="complete"
            element={
              <ProtectedRoute>
                <SetupRouteGuard>
                  <SetupCompleteScreen />
                </SetupRouteGuard>
              </ProtectedRoute>
            }
          />
        </Route>
        {/* Interview Portal - Protected */}
        <Route
          path="/interview/:applicationId"
          element={
            <ProtectedRoute>
              <InterviewPortal />
            </ProtectedRoute>
          }
        />

        {/* Job Routes - Public */}
        <Route path="/job/:jobId" element={<ViewJobPost />} />
        <Route
          path="/report/:candidateID"
          element={<PublicApplicantReport />}
        />
        {/* Job Routes - Protected */}

        <Route
          path="/job/:jobId/apply"
          element={
            <ProtectedRoute>
              <JobApplyDocuments />
            </ProtectedRoute>
          }
        />

        {/* Dashboard Routes */}
        <Route
          path={ROUTES.BOARD}
          element={
            <ProtectedRoute>
              <BoardTabLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<BoardDashboardPage />} />
          <Route path="dashboard/create-job/:jobId" element={<CreateJob />} />
          <Route path="dashboard/job/:id" element={<JobPostingDetails />} />
          <Route
            path="dashboard/job/:id/:tab"
            element={<JobPostingDetails />}
          />
          <Route
            path="analytics"
            element={
              <ProtectedRoute
                allowedRoles={[
                  USER_ROLES.ROOT_USER,
                  USER_ROLES.ADMIN,
                  USER_ROLES.RECRUITER,
                ]}
              >
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="templates"
            element={
              <div className="text-center text-sm text-slate-500">
                Templates view coming soon.
              </div>
            }
          />
          <Route
            path="team"
            element={
              <ProtectedRoute>
                <TeamMembersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="team/add-member"
            element={
              <ProtectedRoute>
                <AddTeamMemberPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="notifications"
            element={
              <div className="text-center text-sm text-slate-500">
                Notifications view coming soon.
              </div>
            }
          />
          <Route
            path="settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Default redirect to onboarding welcome - already handled above */}
      </Routes>
    </CandidateRouteGuard>
  );
}
